/**
 * Single source of truth for which data-entry actions a given vehicle supports.
 *
 * Vehicle energy types map to action kinds:
 *   ev      → trip + charging
 *   gas     → trip + fuel
 *   hybrid  → trip + charging + fuel
 *
 * For "All Vehicles" or when no vehicle is selected, we expose the UNION of
 * all kinds (filtered by what the user's fleet actually contains) so the
 * adaptive UI never offers an action the user can't act on.
 */
import { Route, Zap, Fuel } from "lucide-react";

export const ENTRY_KINDS = {
  trip: {
    id: "trip",
    label: "Trip",
    description: "Log a drive — distance, odometer, battery / fuel telemetry.",
    icon: Route,
    accent: "#E82127",
  },
  charging: {
    id: "charging",
    label: "Charging",
    description: "Record a charging session — kWh, cost, battery %.",
    icon: Zap,
    accent: "#34D17A",
  },
  fuel: {
    id: "fuel",
    label: "Fuel",
    description: "Record a fuel fill-up — liters/gallons, cost, price/unit.",
    icon: Fuel,
    accent: "#FFB340",
  },
};

const PER_ENERGY_KIND = {
  ev: ["trip", "charging"],
  gas: ["trip", "fuel"],
  hybrid: ["trip", "charging", "fuel"],
};

/** Returns the array of entry-kind ids available for a given vehicle. */
export function kindsForVehicle(vehicle) {
  if (!vehicle) return [];
  return PER_ENERGY_KIND[vehicle.energy_type] || ["trip"];
}

/**
 * For an arbitrary collection of vehicles (e.g. "All Vehicles" view), the
 * union of every kind that's supported by at least one vehicle in the set.
 * Order is preserved: trip → charging → fuel.
 */
export function kindsForFleet(vehicles = []) {
  const seen = new Set();
  vehicles.forEach((v) => kindsForVehicle(v).forEach((k) => seen.add(k)));
  return ["trip", "charging", "fuel"].filter((k) => seen.has(k));
}

/** Which kinds are valid as an IMPORT target (all of them, today). */
export function importableKindsForFleet(vehicles = []) {
  return kindsForFleet(vehicles);
}

/** Which kinds are valid for a single vehicle when importing. */
export function importableKindsForVehicle(vehicle) {
  return kindsForVehicle(vehicle);
}
